"use client";

import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { MediaLightbox } from "@/features/products/ui/components/MediaLightbox";
import { Image } from "@/lib/client/components/Image";
import type { MediaDisplay } from "@/lib/shared/types/interfaces";
import { HStack, Text, VStack } from "@/ui";
import { TryOnReferenceNote } from "./TryOnReferenceNote";

interface GalleryItem {
  id: string;
  src: string;
  thumbnailSrc: string;
  alt: string | null;
}

interface CommunityTryOnsGalleryProps {
  sku: string;
}

export function CommunityTryOnsGallery(props: CommunityTryOnsGalleryProps) {
  const { sku } = props;
  const t = useTranslations("virtual_try_on");
  const locale = useLocale();
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const lightboxItems: MediaDisplay[] = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        src: item.src,
        thumbnailSrc: item.thumbnailSrc,
        alt: item.alt,
      })),
    [items],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/${locale}/api/virtual-try-on?sku=${encodeURIComponent(sku)}`);
        if (!res.ok) return;
        const data = (await res.json()) as { items?: GalleryItem[] };
        if (!cancelled) {
          setItems(data.items ?? []);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locale, sku]);

  if (!loaded || items.length === 0) {
    return null;
  }

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <VStack className="w-full gap-3">
      <Text className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
        {t("community_gallery_title")}
      </Text>
      <TryOnReferenceNote compact />
      <HStack className="gap-3 overflow-x-auto pb-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleImageClick(index)}
            className="relative h-24 w-24 shrink-0 overflow-hidden rounded-md border cursor-pointer transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t("community_image_zoom")}
          >
            <Image
              src={item.thumbnailSrc || item.src}
              alt={item.alt ?? t("community_image_alt")}
              fill
              sizes="96px"
              className="object-cover pointer-events-none"
            />
          </button>
        ))}
      </HStack>
      <MediaLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        items={lightboxItems}
        initialIndex={lightboxIndex}
      />
    </VStack>
  );
}
